import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import KPICard from '../shared/KPICard';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

const ACCENT_FILL = {
  '#CC1A00': 'rgba(204,26,0,0.10)',
  '#1E40AF': 'rgba(30,64,175,0.10)',
  '#065F46': 'rgba(6,95,70,0.10)',
};

function ChartCard({ title, children }) {
  return (
    <div style={{
      background: 'white', borderRadius: 12, border: '1px solid #E8EAED',
      padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{title}</div>
      {children}
    </div>
  );
}

const baseTickOpts = { font: { size: 10, family: 'Inter, sans-serif' }, color: '#9CA3AF' };

function emailToLabel(email) {
  const local = (email || '').split('@')[0];
  const parts = local.split('.');
  if (parts.length >= 2) return parts[0] + ' ' + parts[1][0].toUpperCase() + '.';
  return local;
}

export default function OverviewPage({ metrics, thumbs, monthLabels, monthlyQueries, cumulativeUsers, topUsers, devCount, accent }) {
  const ac   = accent || '#CC1A00';
  const fill = ACCENT_FILL[ac] || 'rgba(204,26,0,0.10)';
  const th   = thumbs || { up: 0, neutral: 0, down: 0 };
  const m    = metrics || {};

  // Top users are computed server-side (respecting the exclude-devs toggle).
  const filteredUsers = topUsers || [];
  const topLabels = filteredUsers.map(u => emailToLabel(u.email));
  const topValues = filteredUsers.map(u => u.query_count);

  const kpis = [
    { title: 'Total Users',          value: m.total_users ?? '—',                    subtitle: (devCount || 0) + ' developer IDs registered',  iconName: 'users',    iconColor: '#2563EB', iconBg: '#EFF6FF' },
    { title: 'Total Queries',        value: (m.total_queries ?? 0).toLocaleString(), subtitle: 'All time',                                   iconName: 'chat',     iconColor: ac,        iconBg: ac + '18' },
    { title: 'Queries Today',        value: m.queries_today ?? '—',                  subtitle: 'Since midnight',                             iconName: 'barChart', iconColor: '#0891B2', iconBg: '#ECFEFF' },
    { title: 'Queries Yesterday',    value: m.queries_yesterday ?? '—',              subtitle: 'Previous calendar day',                      iconName: 'barChart', iconColor: '#EA580C', iconBg: '#FFF7ED' },
    { title: 'Queries This Week',    value: m.queries_week ?? '—',                   subtitle: 'Last 7 days',                                iconName: 'barChart', iconColor: '#D97706', iconBg: '#FFFBEB' },
    { title: 'Avg Queries / User',   value: m.avg_per_user ?? '—',                   subtitle: 'Queries per user (all time)',                 iconName: 'overview', iconColor: '#7C3AED', iconBg: '#F5F3FF' },
    { title: 'Thumbs-Up Rate',       value: (m.thumbs_up_rate ?? 0) + '%',           subtitle: th.up + ' / ' + (th.up + th.neutral + th.down) + ' responses',   iconName: 'thumbUp',   iconColor: '#15803D', iconBg: '#F0FDF4' },
    { title: 'Thumbs-Down Rate',     value: (m.thumbs_down_rate ?? 0) + '%',         subtitle: th.down + ' / ' + (th.up + th.neutral + th.down) + ' responses', iconName: 'thumbDown', iconColor: '#DC2626', iconBg: '#FEF2F2' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        {kpis.map((k, i) => <KPICard key={i} {...k} />)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <ChartCard title="Queries Over Time — Monthly">
          <div style={{ height: 180 }}>
            <Line
              data={{
                labels: monthLabels || [],
                datasets: [{
                  label: 'Queries',
                  data: monthlyQueries || [],
                  borderColor: ac,
                  backgroundColor: fill,
                  fill: true,
                  tension: 0.42,
                  pointRadius: 3,
                  pointBackgroundColor: ac,
                  borderWidth: 2.5,
                  pointHoverRadius: 5,
                }],
              }}
              options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ' ' + ctx.parsed.y.toLocaleString() + ' queries' } } },
                scales: {
                  x: { grid: { display: false }, ticks: { ...baseTickOpts, maxTicksLimit: 9 } },
                  y: { grid: { color: '#F3F4F6' }, ticks: baseTickOpts, beginAtZero: false },
                },
              }}
            />
          </div>
        </ChartCard>

        <ChartCard title="User Growth — Cumulative">
          <div style={{ height: 180 }}>
            <Bar
              data={{
                labels: monthLabels || [],
                datasets: [{
                  label: 'Cumulative Users',
                  data: cumulativeUsers || [],
                  backgroundColor: '#2563EB',
                  borderRadius: 3,
                  borderSkipped: false,
                }],
              }}
              options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ' ' + ctx.parsed.y + ' users' } } },
                scales: {
                  x: { grid: { display: false }, ticks: { ...baseTickOpts, maxTicksLimit: 9 } },
                  y: { grid: { color: '#F3F4F6' }, ticks: baseTickOpts, beginAtZero: true },
                },
              }}
            />
          </div>
        </ChartCard>

        <ChartCard title="Message Feedback Sentiment">
          <div style={{ height: 180 }}>
            <Doughnut
              data={{
                labels: ['Thumbs Up', 'Neutral', 'Thumbs Down'],
                datasets: [{
                  data: [th.up, th.neutral, th.down],
                  backgroundColor: ['#15803D', '#D1D5DB', '#DC2626'],
                  borderWidth: 0,
                  hoverOffset: 6,
                }],
              }}
              options={{
                responsive: true, maintainAspectRatio: false,
                cutout: '68%',
                plugins: {
                  legend: { position: 'right', labels: { font: { size: 12, family: 'Inter, sans-serif' }, color: '#374151', padding: 16, boxWidth: 12 } },
                  tooltip: { callbacks: { label: ctx => ' ' + ctx.parsed + ' responses' } },
                },
              }}
            />
          </div>
        </ChartCard>

        <ChartCard title="Top Users by Query Volume">
          <div style={{ height: Math.max(160, filteredUsers.length * 32) }}>
            <Bar
              data={{
                labels: topLabels,
                datasets: [{
                  label: 'Queries',
                  data: topValues,
                  backgroundColor: topValues.map((_, i) => i === 0 ? ac : ac + 'AA'),
                  borderRadius: 3,
                  borderSkipped: false,
                }],
              }}
              options={{
                indexAxis: 'y',
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ' ' + ctx.parsed.x + ' queries' } } },
                scales: {
                  x: { grid: { color: '#F3F4F6' }, ticks: baseTickOpts, beginAtZero: true },
                  y: { grid: { display: false }, ticks: { font: { size: 11, family: 'Inter, sans-serif' }, color: '#374151' } },
                },
              }}
            />
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
